import React, { useEffect, useState, useMemo } from "react";
import {
  useBranchesByCompany,
  useCompanies,
  useCreateBranch,
  useUpdateBranch,
  useBranch,
  useDeleteBranch,
} from "../../../api/queries/organizationQueries";
import EmployeePicker from "../../../components/ui/EmployeePicker";
import SafeDeleteDialog from "../../../components/ui/SafeDeleteDialog";
import { getApiErrorMessage } from "../../../utils/apiError";
import Layout from "../../../components/layout/Layout";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import { Link } from "react-router-dom";
import BranchLocationDialog, {
  hasBranchLocation,
} from "./BranchLocationDialog";
import { BranchChart } from "./OrgTreeDialogs";
import { toast } from "react-toastify";
import { useCanModify } from "../../../hooks/usePermissions";
import {
  NeedCompanyHint,
  useAllCompanies,
} from "../../../components/layout/WorkingCompany";

const VIEW_KEY = "hris.branches.view";
const readView = () => {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === "chart" || v === "list" ? v : "table";
  } catch {
    return "table";
  }
};

/** The company of a branch row (the API names the field differently in places). */
const branchCompanyOf = (b) =>
  b?.companyID || b?.companyId || b?.company?.id || "";

const Branch = () => {
  // View-only roles see the list without create / edit / status actions
  const canModify = useCanModify("company-setup", { company: true });
  const companyId = localStorage.getItem("companyId") || "";
  // Super admin on "All companies": branches are listed per company
  const allCompanies = useAllCompanies();

  // Server data
  const { data: branches = [] } = useBranchesByCompany(companyId);
  const { data: companyList = [] } = useCompanies();
  const createBranch = useCreateBranch();
  const updateBranch = useUpdateBranch();
  const deleteBranch = useDeleteBranch();
  const [deleting, setDeleting] = useState(null); // branch
  // Branch manager (picker value); loaded from GET /companies/branch/:id when editing
  const [manager, setManager] = useState(null);
  const [managerTouched, setManagerTouched] = useState(false);
  // Zoho folders the sync scans; loaded from GET /companies/branch/:id when
  // editing, and only sent when changed so other edits can't clear them
  const [folders, setFolders] = useState({ source: "", target: "" });
  const [foldersTouched, setFoldersTouched] = useState(false);

  /* ===================== LOCAL STATE ===================== */
  const [showModal, setShowModal] = useState(false);
  // Table (filters, sort, paging), chart or list (both add sub-branches)
  const [view, setViewState] = useState(readView);
  const setView = (v) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* private mode */
    }
  };
  const [showConfirm, setShowConfirm] = useState(false);
  const [locationFor, setLocationFor] = useState(null); // branch whose check-in location is edited
  const [pendingBranch, setPendingBranch] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [filters, setFilters] = useState({
    branchName: "",
    parentCompany: "",
    parentBranch: "",
    region: "All",
    status: "All",
  });

  const [formData, setFormData] = useState({
    id: "",
    branchName: "",
    region: "Luzon",
    parentId: "",
    companyId: "",
    isActive: true,
  });

  /* ===================== PAGINATION & SORT STATE ===================== */
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "branchName",
    direction: "asc",
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortConfig]);

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="bi bi-arrow-down-up ms-2 opacity-25 fs-060"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-sort-down ms-2 text-primary fs-070"></i>
    ) : (
      <i className="bi bi-sort-up ms-2 text-primary fs-070"></i>
    );
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const handleOpenAdd = (parent = null) => {
    setIsEditMode(false);
    setFormData({
      id: "",
      branchName: "",
      branchCode: "",
      // A sub-branch starts in its parent's region
      region: parent?.region || "Luzon",
      parentId: parent?.id || "",
      companyId: companyId,
      isActive: true,
    });
    setManager(null);
    setManagerTouched(false);
    setFolders({ source: "", target: "" });
    setFoldersTouched(false);
    setShowModal(true);
  };

  const handleOpenEdit = (branch) => {
    setIsEditMode(true);
    setFormData({
      id: branch.id,
      branchName: branch.branchName || "",
      branchCode: branch.branchCode || "",
      region: branch.region || "Luzon",
      parentId: branch.parentId || "",
      companyId: branchCompanyOf(branch),
      isActive: branch.isActive ?? true,
    });
    setManager(branch.branchManager || null);
    setManagerTouched(false);
    setFolders({
      source: branch.zohoSourceFolderId || "",
      target: branch.zohoTargetFolderId || "",
    });
    setFoldersTouched(false);
    setShowModal(true);
  };

  const handleStatusToggle = (branch) => {
    if (branch.isActive) {
      setPendingBranch(branch);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(branch);
    }
  };

  const executeStatusUpdate = async (branch) => {
    try {
      await updateBranch.mutateAsync({
        id: branch.id,
        data: { isActive: !branch.isActive },
      });
      toast.success("Status updated successfully!");
      setShowConfirm(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Update failed."));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      branchCode: formData.branchCode?.trim() || undefined,
      parentId: formData.parentId || null,
      companyId: formData.companyId || companyId,
    };
    // Only send the manager when it was picked/cleared (or on create)
    if (!formData.id || managerTouched)
      data.branchManagerId = manager?.id || null;
    if (foldersTouched) {
      data.zohoSourceFolderId = folders.source.trim() || null;
      data.zohoTargetFolderId = folders.target.trim() || null;
    }
    try {
      if (formData.id)
        await updateBranch.mutateAsync({ id: formData.id, data });
      else await createBranch.mutateAsync(data);
      toast.success("Saved successfully!");
      setShowModal(false);
    } catch (err) {
      console.error("Save Error:", err);
      toast.error(getApiErrorMessage(err, "Operation failed."));
    }
  };

  const editingBranch = useBranch(
    showModal && formData.id ? formData.id : null,
  );
  useEffect(() => {
    if (editingBranch.data && !managerTouched)
      setManager(editingBranch.data.branchManager || null);
    if (editingBranch.data && !foldersTouched)
      setFolders({
        source: editingBranch.data.zohoSourceFolderId || "",
        target: editingBranch.data.zohoTargetFolderId || "",
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBranch.data]);

  /* ===================== FILTER & PAGINATION LOGIC ===================== */
  const allBranches = useMemo(() => {
    const flattenTree = (nodes) => {
      let flat = [];
      nodes.forEach((node) => {
        const { children, ...rest } = node;
        flat.push(rest);
        if (children && children.length > 0)
          flat = [...flat, ...flattenTree(children)];
      });
      return flat;
    };
    return Array.isArray(branches) ? flattenTree(branches) : [];
  }, [branches]);
  // A parent branch is in the branch's company or one of its parent
  // companies (e.g. MBHC's "Wizard Manpower…" branch over Wizard Luzon)
  const formCompanyId = formData.companyId || companyId;
  const companiesUpFrom = (id) => {
    const ids = new Set();
    for (let cur = id; cur && !ids.has(cur);) {
      ids.add(cur);
      cur = companyList.find((c) => c.id === cur)?.parentId || null;
    }
    return ids;
  };
  const allowedParentCompanies = companiesUpFrom(formCompanyId);
  const parentOptions = allBranches.filter(
    (b) =>
      b.id !== formData.id && allowedParentCompanies.has(branchCompanyOf(b)),
  );
  const currentParent = allBranches.find((b) => b.id === formData.parentId);
  // Unknown parent (not in this list): leave it to the backend
  const parentInOtherCompany =
    !!currentParent &&
    !allowedParentCompanies.has(branchCompanyOf(currentParent));
  const companyNameOf = (id) =>
    companyList.find((c) => c.id === id)?.companyName;
  // Chart nodes carry fewer fields; prefer the full branch row
  const fullBranch = (node) =>
    allBranches.find((b) => b.id === node.id) || node;

  const processedData = useMemo(() => {
    let items = [...allBranches];

    items = items.filter((item) => {
      const nameMatch = (item.branchName || "")
        .toLowerCase()
        .includes(filters.branchName.toLowerCase());
      const companyMatch = (item.company?.companyName || "")
        .toLowerCase()
        .includes(filters.parentCompany.toLowerCase());
      const parentMatch = (item.parent?.branchName || "")
        .toLowerCase()
        .includes(filters.parentBranch.toLowerCase());
      const regionMatch =
        filters.region === "All" || item.region === filters.region;
      const statusMatch =
        filters.status === "All" ||
        (item.isActive ? "Active" : "Inactive") === filters.status;
      return (
        nameMatch && companyMatch && parentMatch && regionMatch && statusMatch
      );
    });

    return items.sort((a, b) => {
      let valA, valB;
      const key = sortConfig.key;
      if (key === "company") {
        valA = (a.company?.companyName || "").toLowerCase();
        valB = (b.company?.companyName || "").toLowerCase();
      } else if (key === "parent") {
        valA = (a.parent?.branchName || "").toLowerCase();
        valB = (b.parent?.branchName || "").toLowerCase();
      } else {
        valA = (a[key] || "").toString().toLowerCase();
        valB = (b[key] || "").toString().toLowerCase();
      }

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [allBranches, filters, sortConfig]);

  const totalItems = processedData.length;
  const currentData = processedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">Branches</h1>
            <p className="text-muted small mb-0">
              {view !== "table"
                ? "Use + on a branch to add a sub-branch under it. Headcount: own / including sub-branches."
                : "All branches of the company. Switch to Chart or List to see and build the hierarchy."}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <div className="btn-group" role="group" aria-label="View">
              <button
                type="button"
                className={`btn btn-sm ${view === "table" ? "btn-primary" : "btn-light border"}`}
                onClick={() => setView("table")}
                aria-pressed={view === "table"}
              >
                <i className="bi bi-table me-1"></i>Table
              </button>
              <button
                type="button"
                className={`btn btn-sm ${view === "chart" ? "btn-primary" : "btn-light border"}`}
                onClick={() => setView("chart")}
                aria-pressed={view === "chart"}
              >
                <i className="bi bi-diagram-3 me-1"></i>Chart
              </button>
              <button
                type="button"
                className={`btn btn-sm ${view === "list" ? "btn-primary" : "btn-light border"}`}
                onClick={() => setView("list")}
                aria-pressed={view === "list"}
              >
                <i className="bi bi-list-nested me-1"></i>List
              </button>
            </div>
            {canModify && (
              <button
                className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
                onClick={() => handleOpenAdd()}
                disabled={allCompanies}
              >
                <i className="bi bi-plus-lg me-2"></i>
                {view !== "table" ? "Add main branch" : "Add New"}
              </button>
            )}
          </div>
        </div>

        <NeedCompanyHint action="see and add its branches" />

        {view !== "table" && companyId && (
          <div className="admin-card">
            <BranchChart
              companyId={companyId}
              view={view}
              onOpen={
                canModify
                  ? (node) => handleOpenEdit(fullBranch(node))
                  : undefined
              }
              renderActions={
                canModify
                  ? (node) => (
                      <span
                        className="d-inline-flex gap-1"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-light border"
                          title="Add sub-branch"
                          aria-label={`Add sub-branch under ${node.branchName}`}
                          onClick={() => handleOpenAdd(fullBranch(node))}
                        >
                          <i className="bi bi-plus-lg"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border"
                          title="Edit"
                          aria-label={`Edit ${node.branchName}`}
                          onClick={() => handleOpenEdit(fullBranch(node))}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border"
                          title="Check-in location"
                          aria-label={`Location of ${node.branchName}`}
                          onClick={() => setLocationFor(fullBranch(node))}
                        >
                          <i className="bi bi-geo-alt"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border text-danger"
                          title="Delete"
                          aria-label={`Delete ${node.branchName}`}
                          onClick={() => setDeleting(fullBranch(node))}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </span>
                    )
                  : undefined
              }
            />
          </div>
        )}

        {view === "table" && (
          <>
            {/* Filter Row */}
            <div className="row g-2 mb-4">
              <div className="col-md-2">
                <input
                  type="text"
                  className="form-control filter-input"
                  placeholder="Search Branch..."
                  value={filters.branchName}
                  onChange={(e) =>
                    setFilters({ ...filters, branchName: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control filter-input"
                  placeholder="Search Parent Company..."
                  value={filters.parentCompany}
                  onChange={(e) =>
                    setFilters({ ...filters, parentCompany: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control filter-input"
                  placeholder="Search Parent Branch..."
                  value={filters.parentBranch}
                  onChange={(e) =>
                    setFilters({ ...filters, parentBranch: e.target.value })
                  }
                />
              </div>
              <div className="col-md-2">
                <select
                  className="form-select filter-input"
                  value={filters.region}
                  onChange={(e) =>
                    setFilters({ ...filters, region: e.target.value })
                  }
                >
                  <option value="All">All Regions</option>
                  <option>Luzon</option>
                  <option>Visayas</option>
                  <option>Mindanao</option>
                </select>
              </div>
              <div className="col-md-2">
                <select
                  className="form-select filter-input"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                >
                  <option value="All">All Status</option>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </div>
            </div>

            {/* Table Content */}

            <div className="glass-table-card">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="custom-table-header sortable">
                    <tr>
                      {[
                        { label: "BRANCH NAME", key: "branchName" },
                        { label: "REGION", key: "region" },
                        { label: "PARENT COMPANY", key: "company" },
                        { label: "PARENT BRANCH", key: "parent" },
                        { label: "MANAGER", key: null },
                        { label: "LOCATION", key: null },
                        { label: "STATUS", key: "isActive" },
                        { label: "ACTIONS", key: null },
                      ].map((col, idx) => (
                        <th
                          key={idx}
                          className={`py-3 ${idx === 0 ? "ps-4" : ""} border-0 ${col.key === "isActive" ? "text-center" : ""} ${!col.key ? "text-end pe-4" : ""}`}
                          onClick={() => col.key && requestSort(col.key)}
                        >
                          {col.label} {col.key && getSortIcon(col.key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {currentData.map((item) => (
                      <tr key={item.id} className="align-middle">
                        <td className="ps-4 py-3 fw-bold fs-085 text-slate-800">
                          {item.branchName}
                        </td>
                        <td className="fs-080 text-slate-700">{item.region}</td>
                        <td className="fs-080 text-slate-700">
                          {item.company?.companyName || "-"}
                        </td>
                        <td className="fs-080 text-slate-700">
                          {item.parent?.branchName || "-"}
                        </td>
                        <td className="fs-080 text-slate-700">
                          {item.branchManager ? (
                            `${item.branchManager.firstName || ""} ${item.branchManager.lastName || ""}`.trim()
                          ) : item.branchManagerId ? (
                            "Assigned"
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="fs-075">
                          {hasBranchLocation(item) ? (
                            <span className="text-success">
                              <i className="bi bi-geo-alt-fill me-1"></i>
                              {item.radius || 200} m radius
                            </span>
                          ) : (
                            <span
                              className="text-warning-emphasis"
                              title="Check-ins of this branch aren't distance-checked"
                            >
                              Not set
                            </span>
                          )}
                        </td>
                        <td className="text-center">
                          <span
                            className={`status-pill status-pill--${item.isActive ? "green" : "gray"}`}
                          >
                            {toCamelCase(item.isActive ? "Active" : "Inactive")}
                          </span>
                        </td>
                        <td className="pe-4 text-end">
                          {canModify ? (
                            <>
                              <div className="d-flex justify-content-end gap-2">
                                <button
                                  className="btn btn-sm btn-light border"
                                  onClick={() => handleOpenEdit(item)}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-light border"
                                  onClick={() => setLocationFor(item)}
                                  title="Check-in location and radius"
                                >
                                  <i className="bi bi-geo-alt"></i> Location
                                </button>
                                <button
                                  className={`btn btn-sm ${item.isActive ? "btn-outline-danger" : "btn-outline-success"}`}
                                  onClick={() => handleStatusToggle(item)}
                                >
                                  {item.isActive ? "Deactivate" : "Activate"}
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => setDeleting(item)}
                                  title="Delete"
                                  aria-label={`Delete ${item.branchName}`}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </>
                          ) : (
                            <span className="fs-070 text-slate-400">
                              View only
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={currentPage}
                limit={itemsPerPage}
                total={totalItems}
                onPageChange={setCurrentPage}
                onLimitChange={(v) => {
                  setItemsPerPage(v);
                  setCurrentPage(1);
                }}
                pageSizes={[10, 25, 50]}
              />
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <Dialog
          title="Confirm Deactivation"
          onClose={() => setShowConfirm(false)}
          size="sm"
        >
          <div className="text-start">
            <p className="text-muted small">
              Are you sure you want to deactivate{" "}
              <strong>{pendingBranch?.branchName}</strong>?
            </p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger profile-btn"
                onClick={() => executeStatusUpdate(pendingBranch)}
              >
                Yes, Deactivate
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Main Modal */}
      {showModal && (
        <Dialog
          title={
            isEditMode
              ? "Edit Branch"
              : formData.parentId
                ? "Add Sub-branch"
                : "Add New Branch"
          }
          onClose={() => setShowModal(false)}
          size="md"
        >
          <form onSubmit={handleSave}>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="profile-label">Branch Name</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={formData.branchName}
                  onChange={(e) =>
                    setFormData({ ...formData, branchName: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <label htmlFor="branch-code" className="profile-label">
                  Code
                </label>
                <input
                  id="branch-code"
                  type="text"
                  className="form-control"
                  maxLength={20}
                  value={formData.branchCode}
                  onChange={(e) =>
                    setFormData({ ...formData, branchCode: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="profile-label">Parent Branch</label>
                <select
                  className={`form-select ${parentInOtherCompany ? "is-invalid" : ""}`}
                  value={formData.parentId}
                  onChange={(e) =>
                    setFormData({ ...formData, parentId: e.target.value })
                  }
                >
                  <option value="">None (Set as Main)</option>
                  {formData.parentId &&
                    !parentOptions.some((b) => b.id === formData.parentId) && (
                      <option value={formData.parentId}>
                        {currentParent?.branchName || "Current parent"}
                        {parentInOtherCompany ? " (other company)" : ""}
                      </option>
                    )}
                  {parentOptions.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.branchName}
                      {branchCompanyOf(b) !== formCompanyId &&
                      companyNameOf(branchCompanyOf(b))
                        ? ` (${companyNameOf(branchCompanyOf(b))})`
                        : ""}
                    </option>
                  ))}
                </select>
                {parentInOtherCompany && (
                  <div className="invalid-feedback d-block">
                    This parent is not in this company or a parent company. Pick
                    another parent, or None.
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <label className="profile-label">Region</label>
                <select
                  className="form-select"
                  value={formData.region}
                  onChange={(e) =>
                    setFormData({ ...formData, region: e.target.value })
                  }
                >
                  <option>Luzon</option>
                  <option>Visayas</option>
                  <option>Mindanao</option>
                </select>
              </div>
              <div className="col-12">
                <label className="profile-label">Parent Company</label>
                <select
                  className="form-select"
                  required
                  value={formData.companyId}
                  onChange={(e) => {
                    const nextCompany = e.target.value;
                    const parent = allBranches.find(
                      (b) => b.id === formData.parentId,
                    );
                    setFormData({
                      ...formData,
                      companyId: nextCompany,
                      parentId:
                        !parent ||
                        companiesUpFrom(nextCompany).has(
                          branchCompanyOf(parent),
                        )
                          ? formData.parentId
                          : "",
                    });
                  }}
                >
                  <option value="">Select Company</option>
                  {companyList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.companyName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label htmlFor="branch-zoho-source" className="profile-label">
                  Zoho source folder ID{" "}
                  <span className="text-slate-400 fw-normal text-none">
                    (optional)
                  </span>
                </label>
                <input
                  id="branch-zoho-source"
                  className="form-control"
                  autoComplete="off"
                  value={folders.source}
                  onChange={(e) => {
                    setFolders({ ...folders, source: e.target.value });
                    setFoldersTouched(true);
                  }}
                />
                <div className="fs-070 text-slate-500 mt-1">
                  The folder the Zoho sync reads for this branch. Without one,
                  the branch is skipped.
                </div>
              </div>
              <div className="col-md-6">
                <label htmlFor="branch-zoho-target" className="profile-label">
                  Zoho target folder ID{" "}
                  <span className="text-slate-400 fw-normal text-none">
                    (optional)
                  </span>
                </label>
                <input
                  id="branch-zoho-target"
                  className="form-control"
                  autoComplete="off"
                  value={folders.target}
                  onChange={(e) => {
                    setFolders({ ...folders, target: e.target.value });
                    setFoldersTouched(true);
                  }}
                />
                <div className="fs-070 text-slate-500 mt-1">
                  Where linked files go. Empty uses the task's default target
                  folder.
                </div>
              </div>
              <div className="col-12">
                <label htmlFor="branch-manager" className="profile-label">
                  Branch Manager{" "}
                  <span className="text-slate-400 fw-normal text-none">
                    (optional)
                  </span>
                </label>
                <EmployeePicker
                  id="branch-manager"
                  companyId={formData.companyId || companyId}
                  value={manager}
                  onChange={(emp) => {
                    setManager(emp);
                    setManagerTouched(true);
                  }}
                  placeholder="Search an active employee of this company"
                />
                {editingBranch.isFetching && !manager && (
                  <div className="fs-070 text-slate-500 mt-1">
                    Loading the current manager…
                  </div>
                )}
                <div className="fs-070 text-slate-500 mt-1">
                  The manager approves for this branch by default and sees its
                  employees. "Change" then leave empty to remove.
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary profile-btn">
                {isEditMode ? "Save Changes" : "Create Branch"}
              </button>
            </div>
          </form>
        </Dialog>
      )}
      {locationFor && (
        <BranchLocationDialog
          branch={locationFor}
          onClose={() => setLocationFor(null)}
        />
      )}
      {deleting && (
        <SafeDeleteDialog
          title="Delete branch?"
          name={deleting.branchName}
          isActive={deleting.isActive}
          onDelete={() => deleteBranch.mutateAsync(deleting.id)}
          onDeactivate={() =>
            updateBranch.mutateAsync({
              id: deleting.id,
              data: { isActive: false },
            })
          }
          onClose={() => setDeleting(null)}
        />
      )}
    </Layout>
  );
};

export default Branch;
